import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import ProjectsPage from "@/pages/Projects";
import ProjectEditor from "@/pages/ProjectEditor";
import DossierPage from "@/pages/Dossier";
import LibraryPage from "@/pages/Library";
import ManualPage from "@/pages/Manual";
import FundingPage from "@/pages/Funding";
import FestivalsPage from "@/pages/Festivals";
import InicioPage from "@/pages/Inicio";
import TareasPage from "@/pages/Tareas";
import ContactosPage from "@/pages/Contactos";
import LoginPage from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import AdminUsers from "@/pages/AdminUsers";
import AppShell from "@/components/AppShell";
import ProjectShell from "@/components/ProjectShell";
import { AuthProvider, useAuth } from "@/lib/auth";

// Routes that don't require auth (anonymous access allowed)
const PUBLIC_PATHS = new Set(["/login", "/reset-password"]);

function Gate({ children }) {
  const { status, user } = useAuth();
  const location = useLocation();
  const isPublic = PUBLIC_PATHS.has(location.pathname);

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gb-bg text-gb-black/55"
           data-testid="auth-loading">
        <p className="text-sm uppercase tracking-[0.3em]">Comprobando sesión…</p>
      </div>
    );
  }
  // Public routes always render
  if (isPublic) return children;
  // Private routes
  if (status !== "ok") return <LoginPage />;
  // Forced password change: redirect anywhere except /change-password
  if (user?.force_password_change && location.pathname !== "/change-password") {
    return <ChangePassword />;
  }
  return children;
}

// Wraps a page with AppShell (rutas tipo catálogo: dashboard, library, etc.)
const Shell = ({ children, active }) => (
  <AppShell active={active}>{children}</AppShell>
);

// Wraps the project editor with ProjectShell (sidebar contextual del proyecto)
const PrjShell = ({ children }) => (
  <ProjectShell>{children}</ProjectShell>
);

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Gate>
            <Routes>
              {/* Públicas - sin shell */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Internas con shell global */}
              <Route path="/change-password" element={<Shell><ChangePassword /></Shell>} />
              <Route path="/admin/users" element={<Shell active="admin"><AdminUsers /></Shell>} />
              <Route path="/" element={<Shell active="projects"><ProjectsPage /></Shell>} />
              <Route path="/library" element={<Shell active="library"><LibraryPage /></Shell>} />
              <Route path="/manual" element={<Shell active="manual"><ManualPage /></Shell>} />
              <Route path="/funding" element={<Shell active="funding"><FundingPage /></Shell>} />
              <Route path="/festivals" element={<Shell active="festivals"><FestivalsPage /></Shell>} />
              <Route path="/inicio" element={<Shell active="inicio"><InicioPage /></Shell>} />
              <Route path="/tareas" element={<Shell active="tareas"><TareasPage /></Shell>} />
              <Route path="/contactos" element={<Shell active="contactos"><ContactosPage /></Shell>} />
              <Route path="/dossier" element={<Shell><DossierPage /></Shell>} />

              {/* Editor de proyecto con sidebar contextual */}
              <Route path="/project/:id" element={<PrjShell><ProjectEditor /></PrjShell>} />
            </Routes>
          </Gate>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
